import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi, fileUrl } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import {
  Ruler, Loader2, ArrowRight, MapPin, BadgeCheck, ShieldCheck, Play,
  FileText, Download, ExternalLink, Layers, Maximize2, DollarSign, Building2,
  QrCode, Images, Copy, Check, Eye,
} from "lucide-react";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2.5 rounded-lg border border-[#30363D] bg-[#161B22]/70 px-4 py-3" data-testid={`bundle-stat-${label}`}>
    <Icon className="w-4 h-4 text-[#38BDF8] shrink-0" />
    <div className="leading-tight">
      <div className="font-heading font-bold text-sm text-[#F0F6FC]">{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">{label}</div>
    </div>
  </div>
);

const DeliverableCard = ({ icon: Icon, title, desc, viewHref, downloadHref, downloadName, accent, testid }) => (
  <div className="group rounded-2xl border border-[#30363D] bg-[#161B22] p-6 flex flex-col transition-colors hover:border-[#38BDF8]/60" data-testid={testid}>
    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}>
      <Icon className="w-5 h-5" />
    </div>
    <div className="font-heading font-semibold text-[#F0F6FC]">{title}</div>
    <p className="text-sm text-[#8B949E] mt-1.5 flex-1">{desc}</p>
    <div className="flex gap-2 mt-5">
      {viewHref && (
        <a href={viewHref} target="_blank" rel="noreferrer" className="flex-1" data-testid={`${testid}-view`}>
          <Button variant="outline" size="sm" className="w-full border-[#30363D] bg-transparent text-[#F0F6FC] hover:text-[#38BDF8] hover:border-[#38BDF8]">
            <ExternalLink className="w-4 h-4 mr-1.5" /> View
          </Button>
        </a>
      )}
      <a href={downloadHref} download={downloadName} className="flex-1" data-testid={`${testid}-download`}>
        <Button size="sm" className="w-full font-semibold" style={{ background: accent, color: "#0D1117" }}>
          <Download className="w-4 h-4 mr-1.5" /> Download
        </Button>
      </a>
    </div>
  </div>
);

const SetbackDiagram = ({ plan, zone, note, usableLot, lowSide }) => {
  const b = plan.boundary || [];
  if (b.length < 3) return null;
  const px = (p) => (Array.isArray(p) ? p[0] : p.x);
  const py = (p) => (Array.isArray(p) ? p[1] : p.y);
  const xs = b.map(px), ys = b.map(py);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const sb = plan.setbacks || {};
  const fs = Number(sb.front) || 0, sd = Number(sb.side) || 0, rr = Number(sb.rear) || 0;
  const pad = 12;
  const W = maxX - minX + pad * 2, H = maxY - minY + pad * 2;
  const vb = `${minX - pad} ${minY - pad} ${W} ${H}`;
  const envX = minX + sd, envY = minY + fs;
  const envW = maxX - minX - sd * 2, envH = maxY - minY - fs - rr;
  const fp = plan.footprint, cant = plan.cantilever;
  const poly = b.map((p) => `${px(p)},${py(p)}`).join(" ");
  const midX = (minX + maxX) / 2;
  const ar = (H / W) * 100;
  return (
    <div style={{ position: "relative", width: "100%", paddingBottom: `${ar}%` }} className="rounded-xl bg-[#0D1117] border border-[#30363D] overflow-hidden">
    <svg viewBox={vb} preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full" data-testid="setback-diagram">
      {/* lot boundary */}
      <polygon points={poly} fill="rgba(56,189,248,0.07)" stroke="#6E7681" strokeWidth="0.9" />
      {/* buildable setback envelope */}
      <rect x={envX} y={envY} width={envW} height={envH} fill="none" stroke="#38BDF8" strokeWidth="0.8" strokeDasharray="3 2" />
      {/* footprint */}
      {fp && <rect x={fp.x} y={fp.y} width={fp.w} height={fp.h} fill="#34D399" fillOpacity="0.85" stroke="#10B981" strokeWidth="0.5" rx="0.5" />}
      {/* usable lot dimension labels on the buildable envelope */}
      {usableLot && (
        <>
          <text x={midX} y={envY + 4.2} fill="#0D1117" fontSize="3.2" textAnchor="middle" fontWeight="700">{usableLot.front}&#39; usable width</text>
          <text x={envX + 3.2} y={envY + envH / 2} fill="#38BDF8" fontSize="3.2" textAnchor="middle" fontWeight="700" transform={`rotate(-90 ${envX + 3.2} ${envY + envH / 2})`}>{usableLot.side}&#39; usable depth</text>
        </>
      )}
      {/* deck cantilever */}
      {cant && <rect x={cant.x} y={cant.y} width={cant.w} height={cant.h} fill="#34D399" fillOpacity="0.35" stroke="#10B981" strokeWidth="0.4" strokeDasharray="2 1.5" />}
      {/* dimension labels */}
      <text x={midX} y={minY - 3} fill="#8B949E" fontSize="3.4" textAnchor="middle" fontWeight="700">FRONT · WILSON ST{lowSide === "front" ? "  ▼ DOWNHILL" : ""}</text>
      <text x={midX} y={maxY + 6.5} fill="#8B949E" fontSize="3.4" textAnchor="middle" fontWeight="700">{lowSide === "front" ? "REAR · UPHILL" : "REAR · DOWNHILL"}</text>
      <text x={midX} y={envY - 1.5} fill="#38BDF8" fontSize="3" textAnchor="middle">{fs}&#39; front setback</text>
      <text x={midX} y={maxY - rr + 4.5} fill="#38BDF8" fontSize="3" textAnchor="middle">{rr}&#39; rear setback</text>
      <text x={minX + sd - 1.2} y={(minY + maxY) / 2} fill="#38BDF8" fontSize="3" textAnchor="middle" transform={`rotate(-90 ${minX + sd - 1.2} ${(minY + maxY) / 2})`}>{sd}&#39; side</text>
      <text x={maxX - sd + 1.2} y={(minY + maxY) / 2} fill="#38BDF8" fontSize="3" textAnchor="middle" transform={`rotate(90 ${maxX - sd + 1.2} ${(minY + maxY) / 2})`}>{sd}&#39; side</text>
    </svg>
    </div>
  );
};

// Cross-section showing the lot dropping to the street with a street-facing walk-out lower level
const SiteSection = ({ grade }) => {
  const drop = grade?.drop_ft ?? 5;
  const depth = grade?.depth_ft ?? 29.1;
  const streetY = 58, rearY = 48;            // ground level: street (front, low) -> rear pad (high)
  const hx0 = 40, hx1 = 104;                 // house front (street side) .. rear
  const roofApex = 3, upperTop = 11, mainTop = 27, lowerTop = 43, lowerBot = 57;
  const midH = (hx0 + hx1) / 2;
  return (
    <div style={{ position: "relative", width: "100%", paddingBottom: "54%" }} className="rounded-xl bg-[#0D1117] border border-[#30363D] overflow-hidden">
      <svg viewBox="0 0 120 66" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full" data-testid="site-section-diagram">
        {/* rear-pad grade reference dashed across to the street */}
        <line x1="8" y1={rearY} x2={hx1} y2={rearY} stroke="#38BDF8" strokeWidth="0.35" strokeDasharray="2 1.5" />
        {/* grade-drop dimension */}
        <line x1="11" y1={rearY} x2="11" y2={streetY} stroke="#38BDF8" strokeWidth="0.5" />
        <polygon points={`11,${rearY} 9.7,${rearY + 2.4} 12.3,${rearY + 2.4}`} fill="#38BDF8" />
        <polygon points={`11,${streetY} 9.7,${streetY - 2.4} 12.3,${streetY - 2.4}`} fill="#38BDF8" />
        <text x="4" y={(rearY + streetY) / 2 - 1.5} fill="#38BDF8" fontSize="3.6" fontWeight="700">{drop}&#39;</text>
        <text x="4" y={(rearY + streetY) / 2 + 2.6} fill="#38BDF8" fontSize="3.2" fontWeight="700">drop</text>

        {/* house structure */}
        {/* gable roof */}
        <polygon points={`${hx0 - 2},${upperTop} ${hx1 + 2},${upperTop} ${midH},${roofApex}`} fill="#1C222B" stroke="#6E7681" strokeWidth="0.6" />
        {/* upper floor */}
        <rect x={hx0} y={upperTop} width={hx1 - hx0} height={mainTop - upperTop} fill="#161B22" stroke="#6E7681" strokeWidth="0.6" />
        {/* main living */}
        <rect x={hx0} y={mainTop} width={hx1 - hx0} height={lowerTop - mainTop} fill="#161B22" stroke="#6E7681" strokeWidth="0.6" />
        {/* lower / walk-out level */}
        <rect x={hx0} y={lowerTop} width={hx1 - hx0} height={lowerBot - lowerTop} fill="#0D1117" stroke="#34D399" strokeWidth="0.8" />

        {/* ground line rising from street (front, low) to rear (high) */}
        <path d={`M0 ${streetY} L${hx1} ${rearY} L120 ${rearY - 1} L120 66 L0 66 Z`} fill="rgba(52,211,153,0.10)" stroke="#10B981" strokeWidth="0.7" />
        {/* earth against the rear of the lower level (below grade) */}
        <path d={`M${hx0} ${streetY} L${hx1} ${rearY} L${hx1} ${lowerBot} L${hx0} ${lowerBot} Z`} fill="rgba(110,118,129,0.28)" />

        {/* street-facing daylight openings on the lower level (front face) */}
        <rect x={hx0 + 2.5} y={lowerBot - 11} width="13" height="11" fill="rgba(56,189,248,0.45)" stroke="#38BDF8" strokeWidth="0.5" />
        <rect x={hx0 + 17.5} y={lowerBot - 9} width="4.5" height="9" fill="rgba(56,189,248,0.7)" stroke="#38BDF8" strokeWidth="0.5" />
        {/* windows on upper floors (street face) */}
        <rect x={hx0 + 3} y={mainTop + 3} width="6" height="8" fill="rgba(56,189,248,0.35)" stroke="#38BDF8" strokeWidth="0.4" />
        <rect x={hx0 + 3} y={upperTop + 3} width="6" height="7" fill="rgba(56,189,248,0.35)" stroke="#38BDF8" strokeWidth="0.4" />

        {/* floor labels */}
        <text x={midH} y={(upperTop + mainTop) / 2 + 1} fill="#8B949E" fontSize="3.4" textAnchor="middle" fontWeight="700">UPPER</text>
        <text x={midH} y={(mainTop + lowerTop) / 2 + 1} fill="#C9D1D9" fontSize="3.4" textAnchor="middle" fontWeight="700">MAIN LIVING</text>
        <text x={midH} y={(lowerTop + lowerBot) / 2 + 0.8} fill="#34D399" fontSize="3.0" textAnchor="middle" fontWeight="700">WALK-OUT · GARAGE + ENTRY</text>

        {/* context labels */}
        <text x={3} y={9} fill="#8B949E" fontSize="3.2" fontWeight="700">{depth}&#39; deep footprint</text>
        <text x={20} y={64.5} fill="#8B949E" fontSize="3.4" textAnchor="middle" fontWeight="700">WILSON ST (LOW)</text>
        <text x={104} y={rearY - 2} fill="#8B949E" fontSize="3.4" textAnchor="middle" fontWeight="700">REAR PAD (HIGH)</text>
      </svg>
    </div>
  );
};


const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildTakeoffCSV = (bc, name) => {
  const lines = [];
  lines.push(`MAX2 Material Takeoff,${name || ""}`);
  lines.push("");
  lines.push("MATERIAL TAKEOFF (BY TRADE)");
  lines.push(["Category", "Item", "Qty", "Unit", "Unit Cost", "Cost (installed allowance)"].map(csvCell).join(","));
  (bc.materials || []).forEach((m) =>
    lines.push([m.category, m.item, m.qty, m.unit, m.unit_cost, m.cost].map(csvCell).join(",")));
  lines.push(["", "", "", "", "SUBTOTAL", bc.materials_total].map(csvCell).join(","));
  if (bc.room_takeoff?.length) {
    lines.push("");
    lines.push("ROOM-BY-ROOM QUANTITIES");
    lines.push(["Floor", "Room", "Area (sf)", "Flooring (sf)", "Drywall (sf)", "Ceiling (sf)", "Perimeter (lf)", "Doors", "Windows", "Est. Cost"].map(csvCell).join(","));
    bc.room_takeoff.forEach((r) =>
      lines.push([r.floor, r.room, r.area_sf, r.flooring_sf, r.drywall_sf, r.ceiling_sf, r.perimeter_lf, r.doors, r.windows, r.cost].map(csvCell).join(",")));
  }
  return lines.join("\n");
};

const downloadCSV = (text, filename) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function Bundle() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingCeiling, setSavingCeiling] = useState(false);
  const [pdfRegen, setPdfRegen] = useState(false);

  const changeCeiling = async (ft) => {
    if (savingCeiling || Math.round(data?.elevation?.floor_height) === ft) return;
    setSavingCeiling(true);
    try {
      const r = await publicApi.post(`/public/projects/${shareId}/ceiling`, { ceiling_ft: ft });
      setData((d) => ({ ...d, build_cost: r.data.build_cost, elevation: r.data.elevation }));
      if (r.data.pdf_regenerating) {
        setPdfRegen(true);
        setTimeout(() => setPdfRegen(false), 14000);
      }
    } catch (e) {
      /* keep previous state */
    }
    setSavingCeiling(false);
  };

  useEffect(() => {
    publicApi.get(`/public/projects/${shareId}/bundle${typeof window !== "undefined" ? window.location.search : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, [shareId]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0D1117] text-[#F0F6FC] gap-4">
        <p className="text-[#8B949E]">This bundle is unavailable or no longer shared.</p>
        <Link to="/"><Button className="bg-[#38BDF8] text-[#0D1117] font-semibold">Go to ArchiMesh AI</Button></Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0D1117]"><Loader2 className="w-8 h-8 animate-spin text-[#38BDF8]" /></div>;
  }

  const s = data.stats || {};
  const hero = data.hero_render ? fileUrl(data.hero_render) : null;
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/bundle/${shareId}` : "";
  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true); setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="min-h-screen bg-[#0D1117] text-[#F0F6FC]" data-testid="bundle-page">
      <header className="sticky top-0 z-30 h-14 glass border-b border-[#30363D] flex items-center px-5 gap-3">
        <Link to="/" className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-[#38BDF8]" />
          <span className="font-heading font-bold">ArchiMesh<span className="text-[#38BDF8]">AI</span></span>
        </Link>
        <div className="flex-1" />
        <Link to={`/share/${shareId}`} className="hidden sm:block">
          <Button size="sm" variant="outline" className="border-[#30363D] bg-transparent hover:text-[#38BDF8] hover:border-[#38BDF8]" data-testid="bundle-plans-link">
            <Layers className="w-4 h-4 mr-1.5" /> View Plans
          </Button>
        </Link>
        <Link to="/"><Button size="sm" className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold whitespace-nowrap">Make your own <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="relative h-[54vh] min-h-[360px] w-full overflow-hidden">
          {hero
            ? <img src={hero} alt={data.name} className="w-full h-full object-cover" data-testid="bundle-hero-img" />
            : <div className="w-full h-full bg-gradient-to-br from-[#161B22] to-[#0D1117]" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0D1117] via-[#0D1117]/40 to-transparent" />
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <div className="max-w-6xl mx-auto px-6 pb-8">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {data.permit_ready && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#34D399]/15 border border-[#34D399]/40 px-3 py-1 text-xs font-semibold text-[#34D399]" data-testid="bundle-permit-badge">
                  <BadgeCheck className="w-3.5 h-3.5" /> Permit-Ready Set
                </div>
              )}
              {data.setback_compliant && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#38BDF8]/15 border border-[#38BDF8]/40 px-3 py-1 text-xs font-semibold text-[#38BDF8]" data-testid="bundle-setback-badge">
                  <ShieldCheck className="w-3.5 h-3.5" /> Setback Compliant{data.setback_zone ? ` — ${data.setback_zone}` : ""}
                </div>
              )}
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="bundle-name">{data.name}</h1>
            {data.address && (
              <div className="flex items-center gap-1.5 text-[#8B949E] mt-2" data-testid="bundle-address">
                <MapPin className="w-4 h-4" /> {data.address}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-6xl mx-auto px-6 -mt-2">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {s.living_sf ? <Stat icon={Maximize2} label="Living Area" value={`${s.living_sf.toLocaleString()} sf`} /> : null}
          <Stat icon={Building2} label="Stories" value={`${s.stories}${s.roof_deck ? " + Deck" : ""}`} />
          {s.value ? <Stat icon={DollarSign} label="Est. Build" value={`$${Math.round(s.value / 1000).toLocaleString()}k`} /> : null}
          <Stat icon={Layers} label="Lower Level" value={s.basement ? "Walk-Out" : "Slab"} />
        </div>
      </section>

      {/* Setback compliance diagram */}
      {data.site_plan && data.setback_compliant && (
        <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="bundle-setback-section">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-5 h-5 text-[#34D399]" />
            <h2 className="font-heading font-semibold text-lg">Zoning &amp; Setback Compliance</h2>
          </div>
          <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 items-start">
            <div className="rounded-2xl border border-[#30363D] bg-[#161B22] p-4">
              <SetbackDiagram plan={data.site_plan} zone={data.setback_zone} note={data.setback_note} usableLot={data.usable_lot} lowSide={data.grade_section?.low_side} />
              <div className="flex flex-wrap items-center gap-4 mt-3 text-[11px] text-[#8B949E]">
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#34D399]" /> House footprint</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#34D399]/40 border border-[#10B981]" /> Master balcony (floating)</span>
                <span className="flex items-center gap-1.5"><span className="inline-block w-4 h-0 border-t-2 border-dashed border-[#38BDF8]" /> Buildable setback line</span>
              </div>
            </div>
            <div className="rounded-2xl border border-[#34D399]/30 bg-[#0D1117] p-6">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-[#34D399]/15 border border-[#34D399]/40 px-3 py-1 text-xs font-semibold text-[#34D399] mb-4">
                <ShieldCheck className="w-3.5 h-3.5" /> Compliant{data.setback_zone ? ` — ${data.setback_zone}` : ""}
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {[["Front", data.site_plan.setbacks?.front], ["Side", data.site_plan.setbacks?.side], ["Rear", data.site_plan.setbacks?.rear]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-[#30363D] bg-[#161B22] px-3 py-2.5" data-testid={`setback-${k.toLowerCase()}`}>
                    <div className="font-heading font-bold text-lg text-[#F0F6FC]">{v}&#39;</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">{k}</div>
                  </div>
                ))}
              </div>
              {data.usable_lot && (
                <div className="rounded-lg border border-[#38BDF8]/30 bg-[#38BDF8]/5 px-3 py-2.5 mb-3" data-testid="usable-lot">
                  <div className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-mono-cad mb-0.5">Usable Lot</div>
                  <div className="font-heading font-bold text-lg text-[#F0F6FC]">{data.usable_lot.front}&#39; <span className="text-[#8B949E] text-sm font-normal">front</span> × {data.usable_lot.side}&#39; <span className="text-[#8B949E] text-sm font-normal">side</span></div>
                </div>
              )}
              {data.setback_note && <p className="text-sm text-[#8B949E] leading-relaxed">{data.setback_note}</p>}
            </div>
          </div>

          {/* Site grade / walk-out cross-section */}
          {data.grade_section && (
            <div className="mt-6 rounded-2xl border border-[#30363D] bg-[#161B22] p-4" data-testid="bundle-grade-section">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Layers className="w-4 h-4 text-[#34D399]" />
                <h3 className="font-heading font-semibold text-sm text-[#F0F6FC]">Site Grade &amp; Walk-Out — Section</h3>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#34D399]/15 border border-[#34D399]/40 px-2.5 py-0.5 text-[11px] font-semibold text-[#34D399]">
                  {data.grade_section.drop_ft}&#39; grade drop to street
                </span>
              </div>
              <div className="grid md:grid-cols-[1.5fr_1fr] gap-6 items-center">
                <SiteSection grade={data.grade_section} />
                <div>
                  <p className="text-sm text-[#8B949E] leading-relaxed" data-testid="grade-section-note">{data.grade_section.note}</p>
                  <div className="flex flex-wrap items-center gap-4 mt-4 text-[11px] text-[#8B949E]">
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#0D1117] border border-[#34D399]" /> Walk-out lower level</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#6E7681]/40 border border-[#6E7681]" /> Below grade (rear)</span>
                    <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 rounded-sm bg-[#38BDF8]/50 border border-[#38BDF8]" /> Street-facing glazing</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Estimated build cost snapshot */}
      {data.build_cost && (
        <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="bundle-cost-section">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <DollarSign className="w-5 h-5 text-[#34D399]" />
            <h2 className="font-heading font-semibold text-lg">Estimated Build Cost</h2>
            <span className="text-[11px] text-[#8B949E] font-mono-cad">· {data.build_cost.tier}{data.build_cost.ve_credit > 0 ? " · value-engineered" : ""}</span>
            <div className="sm:ml-auto flex items-center gap-2" data-testid="ceiling-switch">
              <span className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">Ceiling</span>
              <div className="inline-flex rounded-lg border border-[#30363D] overflow-hidden">
                {[8, 9, 10].map((ft) => {
                  const active = Math.round(data.elevation?.floor_height) === ft;
                  return (
                    <button
                      key={ft}
                      onClick={() => changeCeiling(ft)}
                      disabled={savingCeiling}
                      data-testid={`ceiling-${ft}ft-btn`}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${active ? "bg-[#34D399] text-[#0D1117]" : "bg-[#161B22] text-[#8B949E] hover:text-[#F0F6FC]"}`}
                    >
                      {ft}′
                    </button>
                  );
                })}
              </div>
              {savingCeiling && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#34D399]" />}
            </div>
          </div>
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-6 items-start">
            <div className="rounded-2xl border border-[#34D399]/30 bg-[#0D1117] p-6">
              {data.build_cost.ve_credit > 0 && (
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#34D399]/15 border border-[#34D399]/40 px-3 py-1 text-xs font-semibold text-[#34D399] mb-3" data-testid="cost-ve-badge">
                  <BadgeCheck className="w-3.5 h-3.5" /> Value-Engineered to Target
                </div>
              )}
              <div className="text-[10px] uppercase tracking-widest text-[#34D399] font-mono-cad mb-1">{data.build_cost.ve_credit > 0 ? "Target Build Budget" : "Total Estimated Cost"}</div>
              <div className="font-heading font-bold text-4xl text-[#F0F6FC]" data-testid="cost-total">${data.build_cost.total.toLocaleString()}</div>
              <div className="flex gap-6 mt-4">
                <div>
                  <div className="font-heading font-bold text-xl text-[#F0F6FC]">${data.build_cost.cost_per_sqft.toLocaleString()}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">per sq ft</div>
                </div>
                <div>
                  <div className="font-heading font-bold text-xl text-[#F0F6FC]">{data.build_cost.total_area.toLocaleString()} sf</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">finished area</div>
                </div>
              </div>
              {data.build_cost.as_designed_total > data.build_cost.total && (
                <p className="text-[11px] text-[#8B949E] mt-4 leading-relaxed" data-testid="cost-asdesigned">
                  As originally designed (Standard finishes): <span className="text-[#C9D1D9] font-semibold">${data.build_cost.as_designed_total.toLocaleString()}</span> — value-engineered down to your ${data.build_cost.total.toLocaleString()} target.
                </p>
              )}
              <p className="text-[11px] text-[#8B949E] mt-3 leading-relaxed" data-testid="ceiling-note">
                Framing priced for <span className="text-[#C9D1D9] font-semibold">{Math.round(data.elevation?.floor_height)}′ ceilings</span> ({data.elevation?.plate}′ plate · {data.elevation?.ridge}′ ridge). Switch the ceiling height above to recalculate studs, drywall &amp; the takeoff.
              </p>
              {pdfRegen && (
                <p className="text-[11px] text-[#38BDF8] mt-2 flex items-center gap-1.5" data-testid="ceiling-pdf-note">
                  <Loader2 className="w-3 h-3 animate-spin" /> Updating the construction-set &amp; listing PDFs…
                </p>
              )}
            </div>
            <div className="rounded-2xl border border-[#30363D] bg-[#161B22] p-5">
              <div className="space-y-2.5">
                {[
                  ["Rooms & finishes", data.build_cost.rooms_total],
                  ["Foundation & walk-out", data.build_cost.foundation_total],
                  ["MEP systems (elec / plumb / HVAC)", data.build_cost.disciplines_total],
                  ...(data.build_cost.earthwork ? [["Site earthwork (grade / cut & fill)", data.build_cost.earthwork.cost]] : []),
                  ...(data.build_cost.retaining_wall ? [["Retaining wall", data.build_cost.retaining_wall.cost]] : []),
                ].map(([label, val]) => (
                  <div key={label} className="flex items-center justify-between text-sm" data-testid={`cost-line-${String(label).slice(0, 8)}`}>
                    <span className="text-[#8B949E]">{label}</span>
                    <span className="font-mono-cad text-[#F0F6FC]">${Number(val).toLocaleString()}</span>
                  </div>
                ))}
                <div className="border-t border-[#30363D] my-2" />
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8B949E]">Subtotal</span>
                  <span className="font-mono-cad text-[#F0F6FC]">${data.build_cost.subtotal.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#8B949E]">Contingency (10%)</span>
                  <span className="font-mono-cad text-[#F0F6FC]">${data.build_cost.contingency.toLocaleString()}</span>
                </div>
                {data.build_cost.ve_credit > 0 && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#8B949E]">{data.build_cost.tier} running total</span>
                      <span className="font-mono-cad text-[#8B949E]">${data.build_cost.ve_running_total.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-testid="cost-ve-credit">
                      <span className="text-[#34D399]">Value-engineering to target budget</span>
                      <span className="font-mono-cad text-[#34D399]">− ${data.build_cost.ve_credit.toLocaleString()}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-between text-sm font-semibold pt-1">
                  <span className="text-[#34D399]">Total</span>
                  <span className="font-mono-cad text-[#34D399]">${data.build_cost.total.toLocaleString()}</span>
                </div>
              </div>
              <p className="text-[11px] text-[#6E7681] mt-4 leading-relaxed">Indicative estimate auto-computed from the current floor plan (rooms, systems, foundation and site grade). Walk-out lower level priced as an unfinished garage/utility shell. Confirm with contractor bids.</p>
            </div>
          </div>
        </section>
      )}
      {/* Construction material quantity takeoff */}
      {data.build_cost?.materials?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-14" data-testid="bundle-materials-section">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Layers className="w-5 h-5 text-[#38BDF8]" />
            <h2 className="font-heading font-semibold text-lg">Construction Material Takeoff</h2>
            <span className="text-[11px] text-[#8B949E] font-mono-cad">· quantities from the {data.build_cost.total_area?.toLocaleString()} sf plan</span>
            <Button
              size="sm"
              onClick={() => downloadCSV(buildTakeoffCSV(data.build_cost, data.name), `${data.name}_material_takeoff.csv`)}
              className="ml-auto font-semibold bg-[#38BDF8] text-[#0D1117] hover:bg-[#38BDF8]/90"
              data-testid="download-takeoff-csv"
            >
              <Download className="w-4 h-4 mr-1.5" /> Download material list (CSV)
            </Button>
          </div>
          <div className="rounded-2xl border border-[#30363D] bg-[#161B22] overflow-hidden">
            {Object.entries(
              data.build_cost.materials.reduce((acc, m) => {
                (acc[m.category] = acc[m.category] || []).push(m);
                return acc;
              }, {})
            ).map(([cat, items]) => (
              <div key={cat} className="border-b border-[#30363D] last:border-0" data-testid={`material-cat-${cat}`}>
                <div className="px-5 py-2.5 bg-[#0D1117] text-[11px] uppercase tracking-widest text-[#38BDF8] font-mono-cad">{cat}</div>
                {items.map((m, i) => (
                  <div key={i} className="flex items-center justify-between gap-4 px-5 py-2.5 text-sm border-t border-[#30363D]/60" data-testid={`material-row-${cat}-${i}`}>
                    <span className="text-[#C9D1D9] flex-1">{m.item}</span>
                    <span className="font-mono-cad text-[#F0F6FC] w-32 text-right">{m.qty.toLocaleString()} {m.unit}</span>
                    <span className="font-mono-cad text-[#8B949E] w-28 text-right">${m.cost.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ))}
            <div className="flex items-center justify-between px-5 py-3 bg-[#0D1117]">
              <span className="font-heading font-semibold text-sm text-[#F0F6FC]">Materials subtotal (installed allowance)</span>
              <span className="font-mono-cad text-[#34D399] font-semibold" data-testid="materials-total">${data.build_cost.materials_total?.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-[11px] text-[#6E7681] mt-3 leading-relaxed">Quantities auto-derived from the real floor-plan dimensions (footprint, wall lengths, framed area, door/window counts). Preliminary — confirm with a GC takeoff.</p>

          {data.build_cost.room_takeoff?.length > 0 && (
            <div className="mt-8" data-testid="room-takeoff">
              <h3 className="font-heading font-semibold text-base mb-3 flex items-center gap-2">
                <Ruler className="w-4 h-4 text-[#34D399]" /> Room-by-Room Quantities
              </h3>
              <div className="rounded-2xl border border-[#30363D] bg-[#161B22] overflow-x-auto">
                <table className="w-full text-sm min-w-[640px]">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-widest text-[#8B949E] font-mono-cad">
                      <th className="text-left px-4 py-3 font-normal">Room</th>
                      <th className="text-right px-3 py-3 font-normal">Area</th>
                      <th className="text-right px-3 py-3 font-normal">Flooring</th>
                      <th className="text-right px-3 py-3 font-normal">Drywall</th>
                      <th className="text-right px-3 py-3 font-normal">Ceiling</th>
                      <th className="text-right px-3 py-3 font-normal">Doors</th>
                      <th className="text-right px-3 py-3 font-normal">Windows</th>
                      <th className="text-right px-4 py-3 font-normal">Est. Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.build_cost.room_takeoff.map((r, i) => (
                      <tr key={i} className="border-t border-[#30363D]/60" data-testid={`room-takeoff-row-${i}`}>
                        <td className="px-4 py-2.5 text-[#C9D1D9]">
                          <span className="text-[#F0F6FC]">{r.room}</span>
                          <span className="text-[10px] text-[#6E7681] font-mono-cad ml-2">{r.floor}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#F0F6FC]">{r.area_sf} sf</td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#8B949E]">{r.flooring_sf} sf</td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#8B949E]">{r.drywall_sf} sf</td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#8B949E]">{r.ceiling_sf} sf</td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#8B949E]">{r.doors}</td>
                        <td className="px-3 py-2.5 text-right font-mono-cad text-[#8B949E]">{r.windows}</td>
                        <td className="px-4 py-2.5 text-right font-mono-cad text-[#34D399]">${r.cost?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}

      {data.video_path && (
        <section className="max-w-6xl mx-auto px-6 mt-14">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Play className="w-5 h-5 text-[#38BDF8]" />
              <h2 className="font-heading font-semibold text-lg">Cinematic Walkthrough</h2>
            </div>
            <a href={fileUrl(data.video_path)} download={`${data.name}_walkthrough.mp4`} data-testid="bundle-video-download">
              <Button size="sm" variant="outline" className="border-[#30363D] bg-transparent text-[#F0F6FC] hover:text-[#38BDF8] hover:border-[#38BDF8]">
                <Download className="w-4 h-4 mr-1.5" /> Download walkthrough (mp4)
              </Button>
            </a>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[#30363D] bg-black">
            <video
              src={fileUrl(data.video_path)}
              poster={hero || undefined}
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video"
              data-testid="bundle-video"
            />
          </div>
        </section>
      )}

      {/* Deliverables */}
      <section className="max-w-6xl mx-auto px-6 mt-14">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-heading font-semibold text-lg">Documents</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {data.listing_pdf_path && (
            <DeliverableCard
              testid="bundle-brochure-card"
              icon={FileText}
              accent="#38BDF8"
              title="Listing Brochure"
              desc="Buyer-facing marketing one-pager — hero render, key stats and gallery highlights."
              viewHref={fileUrl(data.listing_pdf_path)}
              downloadHref={fileUrl(data.listing_pdf_path)}
              downloadName={`${data.name}_listing_brochure.pdf`}
            />
          )}
          {data.construction_pdf_path && (
            <DeliverableCard
              testid="bundle-construction-card"
              icon={Building2}
              accent="#A855F7"
              title="Full Construction Set"
              desc="Permit-review PDF — architectural, structural, MEP, egress, energy and the P.E./R.A. review checklist."
              viewHref={fileUrl(data.construction_pdf_path)}
              downloadHref={fileUrl(data.construction_pdf_path)}
              downloadName={`${data.name}_construction_set.pdf`}
            />
          )}
          {data.book_pdf_path && (
            <DeliverableCard
              testid="bundle-book-card"
              icon={Images}
              accent="#34D399"
              title="Design & Render Book"
              desc="Full presentation book — every room, hallway and exterior at the built measurements, plus the priced material takeoff."
              viewHref={fileUrl(data.book_pdf_path)}
              downloadHref={fileUrl(data.book_pdf_path)}
              downloadName={`${data.name}_design_book.pdf`}
            />
          )}
        </div>
      </section>

      {/* Gallery */}
      {data.gallery?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-14">
          <div className="flex items-center gap-2 mb-4">
            <Maximize2 className="w-5 h-5 text-[#38BDF8]" />
            <h2 className="font-heading font-semibold text-lg">Gallery</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {data.gallery.map((g, i) => (
              <a key={i} href={fileUrl(g.path)} target="_blank" rel="noreferrer"
                 className="group relative aspect-[4/3] rounded-xl overflow-hidden border border-[#30363D]" data-testid={`bundle-gallery-${i}`}>
                <img src={fileUrl(g.path)} alt={g.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-2.5 py-2">
                  <span className="text-[11px] text-white/90 font-medium">{g.label}</span>
                </div>
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Brochure */}
      {data.brochure?.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 mt-14">
          <div className="flex items-center gap-2 mb-4">
            <Images className="w-5 h-5 text-[#38BDF8]" />
            <h2 className="font-heading font-semibold text-lg">Instagram Brochure</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {data.brochure.map((b, i) => (
              <a key={i} href={fileUrl(b)} target="_blank" rel="noreferrer"
                 className="group relative aspect-[4/5] rounded-xl overflow-hidden border border-[#30363D]" data-testid={`bundle-brochure-${i}`}>
                <img src={fileUrl(b)} alt={`Brochure card ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* Share + QR */}
      <section className="max-w-6xl mx-auto px-6 mt-14">
        <div className="rounded-2xl border border-[#30363D] bg-[#161B22] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="bundle-share">
          {data.qr_path && (
            <div className="shrink-0 bg-white rounded-xl p-3">
              <img src={fileUrl(data.qr_path)} alt="Scan to open listing" className="w-36 h-36" data-testid="bundle-qr-img" />
            </div>
          )}
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <div className="flex items-center gap-2 justify-center sm:justify-start">
              <QrCode className="w-5 h-5 text-[#38BDF8]" />
              <h2 className="font-heading font-semibold text-lg">Share this listing</h2>
            </div>
            <p className="text-sm text-[#8B949E] mt-1.5">Scan the code or share the link — opens this full listing with the walkthrough, gallery and brochure.</p>
            {typeof data.views === "number" && (
              <div className="flex items-center gap-2 mt-3 text-xs text-[#6E7681]" data-testid="bundle-view-count">
                <Eye className="w-3.5 h-3.5" />
                <span>Viewed <span className="text-[#8B949E] font-semibold">{data.views.toLocaleString()}</span> {data.views === 1 ? "time" : "times"}{data.qr_views ? <> · <span className="text-[#8B949E] font-semibold">{data.qr_views.toLocaleString()}</span> via QR</> : ""}</span>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 mt-4">
              <div className="flex-1 truncate rounded-lg border border-[#30363D] bg-[#0D1117] px-3 py-2 text-sm text-[#8B949E] font-mono-cad" data-testid="bundle-share-url">{shareUrl}</div>
              <Button size="sm" onClick={copyLink} className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold" data-testid="bundle-copy-link">
                {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy link</>}
              </Button>
              {data.qr_path && (
                <a href={fileUrl(data.qr_path)} download={`${data.name}_QR.png`} data-testid="bundle-qr-download">
                  <Button size="sm" variant="outline" className="w-full border-[#30363D] bg-transparent text-[#F0F6FC] hover:text-[#38BDF8] hover:border-[#38BDF8]">
                    <Download className="w-4 h-4 mr-1.5" /> QR
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="max-w-6xl mx-auto px-6 mt-16 mb-16">
        <div className="rounded-2xl border border-[#30363D] bg-gradient-to-br from-[#161B22] to-[#0D1117] p-8 text-center">
          <h3 className="font-heading font-bold text-2xl">Design your own home with AI</h3>
          <p className="text-[#8B949E] mt-2 text-sm">Blueprints, 3D walkthroughs and permit-ready construction sets — generated in minutes.</p>
          <Link to="/" className="inline-block mt-5">
            <Button className="bg-[#38BDF8] hover:bg-[#0EA5E9] text-[#0D1117] font-semibold">Start free <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
